// Methods in this file are exposed to vanilla JS, hence static

import Helpers from "./helpers";
import SelectorFilter from "./selector-filter";

export default class Tooltip {

	static visibleClass() {
		return 'js-tooltip--visible';
	}

	static showClass() {
		return 'js-tooltip--show';
	}

	static clickedClass() {
		return 'js-tooltip-trigger--clicked';
	}

	static dimmerVisibleClass() {
		return 'js-tooltip-dimmer--visible';
	}

	static dimmer() {
		return document.querySelector('[data-js-tooltip-dimmer]') as HTMLElement;
	}

	constructor() {

		Tooltip.init(true);

	}

	public static init(firstRun: Boolean) {

		if (firstRun) {

			Tooltip.injectDimmer();

			var debouncedReposition = Helpers.debounce(Tooltip.reposition, 250);
			window.addEventListener('resize', debouncedReposition);

			document.addEventListener('click', (e) => Tooltip.documentClick(e));
			document.addEventListener('keyup', (e) => Tooltip.documentKeyUp(e));

		}

		Tooltip.triggers();
		Tooltip.cloneAndMove();
		Tooltip.closeLinks();

	}

	static injectDimmer() {

		document.body.insertAdjacentHTML('beforeend',
			'<div class="js-tooltip-dimmer" data-js-tooltip-dimmer></div>'
		);

		Tooltip.dimmer().addEventListener('click', (e) => Tooltip.dimmerClick(e));

	}

	static hideTooltips() {

		const tooltips = document.querySelectorAll('.js-tooltip');

		for (let i = 0; i < tooltips.length; i++) {

			tooltips[i].classList.remove(Tooltip.showClass());
			tooltips[i].classList.remove(Tooltip.visibleClass());

		}

	}

	static resetTriggers() {

		const triggers = document.querySelectorAll('.js-tooltip-trigger');

		for (let i = 0; i < triggers.length; i++) { triggers[i].classList.remove(Tooltip.clickedClass()); }

	}

	static dimmerClick(e: Event) {

		Tooltip.dimmer().classList.remove(Tooltip.dimmerVisibleClass());
		Tooltip.hideTooltips();
		Tooltip.resetTriggers();

	}

	static cloneAndMove() {

		const tooltips = SelectorFilter.excludeByClass(document.querySelectorAll('[data-js-tooltip]'), 'has-initialised');

		if (!tooltips.length) { return; }

		for (let i = 0; i < tooltips.length; i++) {

			let element = tooltips[i] as HTMLElement;
			element.setAttribute('data-js-tooltip', 'initialised');
			element.classList.add('has-initialised');

			element.querySelector('[data-js-tooltip-inner]').insertAdjacentHTML('beforeend',
				'<button type="button" class="js-tooltip__close" data-test="tooltip-close-button">Close</button>'
			);

			let shouldRetainPositionInDom = element.getAttribute('data-js-tooltip-retain-position-in-dom') || false;

			if (!shouldRetainPositionInDom) {

				let clone = element.cloneNode(true);
				document.body.appendChild(clone);

				element.parentNode.removeChild(element);

			}

		}

	}

	static triggers() {

		const triggers = SelectorFilter.excludeByClass(document.querySelectorAll('[data-js-tooltip-trigger]'), 'has-initialised');

		if (!triggers.length) { return; }

		for (let i = 0; i < triggers.length; i++) {

			let element = triggers[i] as HTMLElement;
			Tooltip.handleCustomAppendTarget(element);

			element.setAttribute('data-js-tooltip-trigger', 'initialised');
			element.classList.add('has-initialised');
			element.addEventListener('click', (e) => Tooltip.triggerClick(e));

		}

	}

	static handleCustomAppendTarget(element: HTMLElement) {

		const appendTargetSelector = element.getAttribute('data-js-tooltip-append-target') || false;
		const appendTarget = appendTargetSelector ? document.querySelector(appendTargetSelector) : false;

		if (!appendTarget) return;

		const id = element.getAttribute('href').replace('#', '');
		const tip = document.querySelector('#' + id) as HTMLElement;
		const tipCloneId = id + '-' + Helpers.guid();
		const tipClone = tip.cloneNode(true) as HTMLElement;

		tipClone.setAttribute('id', tipCloneId);
		tipClone.setAttribute('data-js-tooltip-retain-position-in-dom', 'true');
		appendTarget.appendChild(tipClone);
		element.setAttribute('href', '#' + tipCloneId);

	}

	static triggerClick(e: Event) {

		const target = e.currentTarget as HTMLElement;
		const id = target.getAttribute('href');
		const tip = document.querySelector(id) as HTMLElement;
		const hideDimmer = target.getAttribute('data-js-tooltip-hide-dimmer') || false;

		if (tip.classList.contains(Tooltip.visibleClass())) {
			e.preventDefault();
			return;
		}

		Tooltip.hideTooltips();

		Tooltip.positionTooltip(tip, target);

		tip.classList.add(Tooltip.showClass());
		target.classList.add(Tooltip.clickedClass());

		setTimeout(function () {

			tip.classList.add(Tooltip.visibleClass());
			if (!hideDimmer) { Tooltip.dimmer().classList.add(Tooltip.dimmerVisibleClass()); }

		}, 10);

		e.preventDefault();

	}

	static closeLinks() {

		const links = document.querySelectorAll('.js-tooltip__close');

		if (!links.length) { return; }

		for (let i = 0; i < links.length; i++) {

			let element = links[i] as HTMLElement;

			element.addEventListener('click', (e) => Tooltip.closeLinkClick(e));

		}

	}

	static closeLinkClick(e: Event) {

		Tooltip.dimmer().classList.remove(Tooltip.dimmerVisibleClass());
		Tooltip.hideTooltips();
		Tooltip.resetTriggers();
		e.preventDefault();

	}

	static clampLeftPosition(leftPosition: number, tooltipWidth: number, edgePadding: number): number {

		const viewportLeft = (window.pageXOffset || document.documentElement.scrollLeft || 0);
		const viewportRight = viewportLeft + window.innerWidth;

		if (leftPosition + tooltipWidth > viewportRight - edgePadding) {
			leftPosition = viewportRight - edgePadding - tooltipWidth;
		}

		if (leftPosition < viewportLeft + edgePadding) {
			leftPosition = viewportLeft + edgePadding;
		}

		return leftPosition;

	}

	static positionTooltip(tip: HTMLElement, link: HTMLElement) {

		const position = link.getAttribute('data-js-tooltip-trigger-position') || 'right';

		const offset = Helpers.getOffset(link);
		const linkWidth = link.offsetWidth;
		const additionalSpace = 10;
		const edgePadding = 40;

		if (position === 'right') {

			tip.classList.add(Tooltip.showClass()); // we need to show the element to get its width

			const rightLeft = offset.left + linkWidth + additionalSpace;
			const viewportRight = (window.pageXOffset || document.documentElement.scrollLeft || 0) + window.innerWidth;
			const wouldOverflow = rightLeft + tip.offsetWidth > viewportRight;

			if (!tip.classList.contains(Tooltip.visibleClass())) { tip.classList.remove(Tooltip.showClass()); }

			if (!wouldOverflow) {
				Tooltip.positionRight(tip, rightLeft, offset, additionalSpace);
			} else {
				Tooltip.positionBelow(tip, link, offset, linkWidth, additionalSpace, edgePadding);
			}

		} else if (position === 'bottom') {
			Tooltip.positionBelow(tip, link, offset, linkWidth, additionalSpace, edgePadding);
		}

	}

	static positionRight(tip: HTMLElement, rightLeft: number, offset: any, additionalSpace: number) {
		tip.style.left = rightLeft + 'px';
		tip.style.top = offset.top - additionalSpace + 'px';
	}

	static positionBelow(tip: HTMLElement, link: HTMLElement, offset: any, linkWidth: number, additionalSpace: number, edgePadding: number) {

		tip.classList.add(Tooltip.showClass()); // we need to show the element to get its width

		const leftPosition = Tooltip.clampLeftPosition(
			offset.left - (tip.offsetWidth / 2) - (linkWidth / 2),
			tip.offsetWidth,
			edgePadding
		);

		tip.style.left = leftPosition + 'px';
		tip.style.top = offset.top + link.offsetHeight + additionalSpace + 'px';

		if (!tip.classList.contains(Tooltip.visibleClass())) { tip.classList.remove(Tooltip.showClass()); }

	}

	static reposition() {

		const tip = document.querySelector('.' + Tooltip.visibleClass()) as HTMLElement;

		if (!tip) { return; }

		const link = document.querySelector('[data-js-tooltip-trigger][href="#' + tip.getAttribute('id') + '"]') as HTMLElement;

		if (!link) { return; }

		Tooltip.positionTooltip(tip, link);

	}

	static documentClick(e: Event) {

		const target = e.target as HTMLElement;
		const visible = document.querySelector('.' + Tooltip.visibleClass());

		if (!visible) { return; }

		if (target !== visible && !visible.contains(target)) {

			Tooltip.dimmer().classList.remove(Tooltip.dimmerVisibleClass());
			Tooltip.hideTooltips();
			Tooltip.resetTriggers();

		}

	}

	static documentKeyUp(e: KeyboardEvent) {

		var key = Helpers.getKeypressed(e);

		if (key === 'Escape' || key === 'Esc' || key === 27) {

			Tooltip.dimmer().classList.remove(Tooltip.dimmerVisibleClass());
			Tooltip.hideTooltips();
			Tooltip.resetTriggers();

		}

	}

}
